// assets/free-gift.js

const FREE_GIFT_PROPERTY_FLAG = '_free_gift';
const FREE_GIFT_BLOCK_PROP = '_free_gift_block';

let freeGiftConfigCache = null;
let freeGiftCorrecting = false;
let debounceTimer = null;
let justAddedGift = false; // Flag to prevent immediate correction after adding gift

const collectionCache = new Map();

/**
 * Ask the theme's CartElement(s) to refresh their HTML from the cart section.
 */
function refreshThemeCartUI() {
  const cartElements = document.querySelectorAll('cart-element');
  
  cartElements.forEach((el) => {
    if (typeof el.getCart === 'function') {
      try {
        el.getCart();
      } catch (e) {
        console.warn('[Free Gift] Failed to call CartElement.getCart()', e);
      }
    }
  });
}

/**
 * Load and cache free gift configuration from <script data-free-gift-json>.
 */
function getFreeGiftConfig() {
  if (freeGiftConfigCache) {
    return freeGiftConfigCache;
  }

  const script = document.querySelector('[data-free-gift-json]');
  if (!script) {
    console.warn('[Free Gift] ❌ No config script found with [data-free-gift-json]');
    return null;
  }

  try {
    const data = JSON.parse(script.textContent);
    freeGiftConfigCache = data;
    return data;
  } catch (e) {
    console.error('[Free Gift] ❌ JSON parse error:', e, 'Script content:', script.textContent);
    return null;
  }
}

/**
 * Fetch cart JSON.
 */
async function fetchCart() {
  const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    console.error('[Free Gift] ❌ Cart fetch failed:', res.status, res.statusText);
    throw new Error('Cart fetch failed');
  }
  const cart = await res.json();
  
  // Ensure currency info is available - use window.Shopify.currency as fallback
  // The /cart.js API might not include currency, but window.Shopify.currency does (same as spend-and-save uses)
  const hasValidCurrency = cart.currency && cart.currency.iso_code && cart.currency.iso_code !== 'unknown';
  
  if (!hasValidCurrency) {
    // Get currency from window.Shopify.currency (same as spend-and-save uses)
    const shopifyCurrency = window.Shopify?.currency?.active;
    const shopifyRate = window.Shopify?.currency?.rate;
    
    // Ensure cart.currency is an object
    if (!cart.currency || typeof cart.currency !== 'object') {
      cart.currency = {};
    }
    
    cart.currency.iso_code = shopifyCurrency || cart.currency.iso_code || 'AUD';
    // Convert rate to number (it might be a string from Shopify)
    cart.currency.rate = shopifyRate ? parseFloat(shopifyRate) : (cart.currency.rate ? parseFloat(cart.currency.rate) : 1.0);
  }
  
  return cart;
}

/**
 * Add a single free gift variant with block ID.
 */
async function addGiftVariant(variantId, blockId, quantity = 1) {
  const body = {
    items: [
      {
        id: variantId,
        quantity: quantity,
        properties: {
          [FREE_GIFT_PROPERTY_FLAG]: 'true',
          [FREE_GIFT_BLOCK_PROP]: String(blockId ?? ''),
        },
      },
    ],
  };

  const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error('[Free Gift] ❌ Failed to add gift:', res.status, errorText);
    throw new Error('Failed to add free gift: ' + errorText);
  }

  const result = await res.json();
  return result;
}

/**
 * Update cart line quantities by key.
 */
async function updateCartLines(updates) {

  const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error('Failed to update cart');
  }

  const cart = await res.json();
  return cart;
}

/**
 * Return all free gift lines from cart.
 */
function getGiftLines(cart) {
  return cart.items.filter(
    (item) => item.properties && item.properties[FREE_GIFT_PROPERTY_FLAG]
  );
}

/**
 * Fetch products from a collection via Shopify JSON endpoint, with caching.
 */
async function fetchCollectionProducts(collectionHandle) {
  if (!collectionHandle) return [];

  if (collectionCache.has(collectionHandle)) {
    return collectionCache.get(collectionHandle);
  }

  try {
    const res = await fetch(
      `${window.Shopify.routes.root}collections/${collectionHandle}/products.json`
    );
    if (!res.ok) {
      console.error('[Free Gift] Failed to fetch collection products:', res.status);
      collectionCache.set(collectionHandle, []);
      return [];
    }
    const data = await res.json();
    collectionCache.set(collectionHandle, data.products);
    return data.products;
  } catch (e) {
    console.error('[Free Gift] Error fetching collection products:', e);
    collectionCache.set(collectionHandle, []);
    return [];
  }
}

/**
 * Get product variant ID from product ID or handle
 */
async function getVariantIdFromProduct(productIdOrHandle) {
  const input = productIdOrHandle.trim();
  
  // If it's a numeric string, assume it's a variant ID
  if (/^\d+$/.test(input)) {
    const variantId = parseInt(input);
    return variantId;
  }

  // Try as product handle
  try {
    const productUrl = `${window.Shopify.routes.root}products/${input}.json`;
    const productResponse = await fetch(productUrl);
    
    if (productResponse.ok) {
      const productData = await productResponse.json();
      
      if (productData.product && productData.product.variants && productData.product.variants.length > 0) {
        // Use first available variant, or first variant if none available
        const availableVariant = productData.product.variants.find(v => v.available);
        if (availableVariant) {
          return availableVariant.id;
        }
        const firstVariantId = productData.product.variants[0].id;
        return firstVariantId;
      }
      console.warn('[Free Gift] ⚠️ Product has no variants');
    } else {
      console.error('[Free Gift] ❌ Product fetch failed:', productResponse.status);
    }
  } catch (error) {
    console.error('[Free Gift] ❌ Error fetching product:', error);
  }

  console.warn('[Free Gift] ❌ Could not resolve variant ID from:', input);
  return null;
}

/**
 * Choose a variant ID for a given block.
 * For collections: first available variant in the collection.
 * For products: variant ID from product ID/handle.
 */
async function chooseBlockVariantId(block) {
  if (!block) {
    console.warn('[Free Gift] ❌ chooseBlockVariantId: block is null/undefined');
    return null;
  }

  // Handle product type
  if (block.type === 'product' && block.productId) {
    const variantId = await getVariantIdFromProduct(block.productId);
    return variantId;
  }

  // Handle collection type (existing logic)
  if (block.type === 'collection' && block.collectionHandle) {
    const products = await fetchCollectionProducts(block.collectionHandle);
    if (!products || !products.length) {
      console.warn('[Free Gift] ❌ No products found in collection:', block.collectionHandle);
      return null;
    }

    // First product with at least one available variant, else fall back to first variant found.
    for (const product of products) {
      const variants = product.variants || [];
      const availableVariant = variants.find(v => v.available);
      if (availableVariant) {
        return availableVariant.id;
      }
    }

    // Fallback: just use the first variant of the first product
    const first = products[0];
    if (first && first.variants && first.variants.length) {
      const variantId = first.variants[0].id;
      return variantId;
    }
  }

  // Legacy support: if no type specified, assume collection
  if (block.collectionHandle) {
    const products = await fetchCollectionProducts(block.collectionHandle);
    if (!products || !products.length) {
      console.warn('[Free Gift] ❌ No products found in collection (legacy)');
      return null;
    }

    for (const product of products) {
      const variants = product.variants || [];
      const availableVariant = variants.find(v => v.available);
      if (availableVariant) {
        return availableVariant.id;
      }
    }

    const first = products[0];
    if (first && first.variants && first.variants.length) {
      const variantId = first.variants[0].id;
      return variantId;
    }
  }

  console.warn('[Free Gift] ❌ Could not choose variant ID for block:', block.id);
  return null;
}

/**
 * Check if cart subtotal qualifies for a block.
 * minValue / maxValue are in AUD (store base currency), cart subtotal is in cents.
 * maxValue = 0 means "no upper limit".
 * Converts minValue/maxValue from AUD to current cart currency before comparison.
 * Uses the same conversion logic as spend-and-save feature.
 */
function isCartEligible(block, cart) {
  // Use cart.total_price (same as spend-and-save feature)
  // cart.total_price already excludes free gift items (they have price of 0)
  // This ensures both features use the same calculation
  const subtotal = cart.total_price || 0;
  
  const hasMin = !!block.minValue;
  const hasMax = !!block.maxValue;

  // Use same conversion logic as spend-and-save feature
  // Get currency info - prefer window.Shopify.currency (same as spend-and-save uses)
  // This is more reliable than cart.currency from /cart.js API
  const shopifyCurrency = window.Shopify?.currency?.active;
  const shopifyRate = window.Shopify?.currency?.rate;
  
  // Use window.Shopify.currency if available, otherwise fall back to cart.currency
  const cartCurrencyCode = shopifyCurrency || cart.currency?.iso_code || 'AUD';
  // Convert rate to number (it might be a string from Shopify)
  const currencyRate = shopifyRate ? parseFloat(shopifyRate) : (cart.currency?.rate ? parseFloat(cart.currency.rate) : 1.0);
  
  // Convert minValue/maxValue from AUD (dollars) to cents, then apply currency rate if not AUD
  // Same logic as spend-and-save: if cart.currency.iso_code != 'AUD' and currency_rate > 0
  let minValueConverted = block.minValue ? block.minValue * 100 : 0;
  let maxValueConverted = block.maxValue && block.maxValue > 0 ? block.maxValue * 100 : null;
  
  if (cartCurrencyCode !== 'AUD' && currencyRate > 0) {
    minValueConverted = minValueConverted * currencyRate;
    if (maxValueConverted) {
      maxValueConverted = maxValueConverted * currencyRate;
    }
  }

  const minOk = hasMin ? subtotal >= minValueConverted : true;
  const maxOk = hasMax && block.maxValue > 0 ? subtotal <= maxValueConverted : true;
  const eligible = minOk && maxOk;

  return eligible;
}

/**
 * Main correction logic: keep gifts in sync with rules (auto blocks).
 * Accepts a cart object and returns { mutated: boolean }.
 */
async function correctFreeGifts(cart) {
  if (freeGiftCorrecting) {
    return { mutated: false };
  }
  // Skip correction if we just added a gift (prevent race condition)
  if (justAddedGift) {
    justAddedGift = false;
    return { mutated: false };
  }
  freeGiftCorrecting = true;

  const config = getFreeGiftConfig();
  const giftLines = getGiftLines(cart);
  const updates = {};
  const giftsToAdd = [];

  // If no config or blocks, remove all existing gift items
  if (!config || !config.blocks || !config.blocks.length) {
    console.warn('[Free Gift] No config or blocks found, removing all gift items');
    giftLines.forEach((giftLine) => {
      if (giftLine && giftLine.key) {
        updates[giftLine.key] = 0;
      }
    });
  } else {
    // Process each configured block
    for (const block of config.blocks) {
      if (!block || !block.id) continue;

      const blockId = block.id;
      const eligible = isCartEligible(block, cart);

      const giftLine = giftLines.find(
        (line) => line.properties && line.properties[FREE_GIFT_BLOCK_PROP] === String(blockId)
      );

      if (eligible) {
        if (giftLine) {
          // Check if quantity matches configured quantity, update if it doesn't
          // Only update if block has a quantity property (product type), otherwise skip for collection types
          if (block.quantity !== undefined) {
            const configuredQuantity = block.quantity || 1;
            if (giftLine.quantity !== configuredQuantity) {
              updates[giftLine.key] = configuredQuantity;
            }
          }
          continue;
        }

        if (block.triggerType === 'automatic') {
          const variantId = await chooseBlockVariantId(block);
          if (variantId) {
            const quantity = block.quantity || 1;
            giftsToAdd.push({ variantId, blockId, quantity, message: block.message });
          } else {
            console.warn('[Free Gift] No variant available for block:', blockId, 'productId:', block.productId);
          }
        }
      } else {
        if (giftLine) {
          updates[giftLine.key] = 0;
        }
      }
    }

    // Remove any gift items that don't match any configured block
    const configuredBlockIds = config.blocks.map(block => String(block.id)).filter(Boolean);
    giftLines.forEach((giftLine) => {
      if (giftLine && giftLine.properties) {
        const giftBlockId = giftLine.properties[FREE_GIFT_BLOCK_PROP];
        if (giftBlockId && !configuredBlockIds.includes(String(giftBlockId))) {
          updates[giftLine.key] = 0;
        }
      }
    });
  }

  let mutated = false;

  try {
    if (Object.keys(updates).length) {
      justAddedGift = true; // Prevent correction from running again after update
      await updateCartLines(updates);
      mutated = true;
      // Clear flag after a delay to allow cart to update
      setTimeout(() => {
        justAddedGift = false;
      }, 1000);
    }

    for (const gift of giftsToAdd) {
      try {
        justAddedGift = true; // Set flag to prevent immediate correction
        await addGiftVariant(gift.variantId, gift.blockId, gift.quantity || 1);
        // Show message if configured
        if (gift.message) {
          showFreeGiftMessage(gift.message);
        }
        mutated = true;
        // Clear flag after a delay to allow cart to update
        setTimeout(() => {
          justAddedGift = false;
        }, 1000);
      } catch (addError) {
        console.error('[Free Gift] Failed to add gift variant:', gift.variantId, 'error:', addError);
        justAddedGift = false; // Clear flag on error
        // Continue with other gifts even if one fails
      }
    }

    if (mutated) {
      // Add a small delay before refreshing to ensure cart API has updated
      setTimeout(() => {
        refreshThemeCartUI();
      }, 200);
    }
  } catch (e) {
    console.error('[Free Gift] Correction failed', e);
  } finally {
    freeGiftCorrecting = false;
  }

  return { mutated };
}

/**
 * Manual "pick your gift" UI (for blocks with triggerType == "manual").
 * Uses collection JSON for products; no block.product assumption.
 */
async function renderManualGifts(cart) {

  const config = getFreeGiftConfig();
  const placeholder = document.querySelector('[data-free-gift-placeholder]');

  if (!config) {
    console.warn('[Free Gift] No config found for manual gifts');
    return;
  }

  if (!placeholder) {
    console.warn('[Free Gift] No placeholder found with [data-free-gift-placeholder]');
    return;
  }

  const giftLines = getGiftLines(cart);
  placeholder.innerHTML = '';

  const manualBlocks = (config.blocks || []).filter((b) => b.triggerType === 'manual');

  for (const block of manualBlocks) {
    if (!block || !block.id) continue;
    const blockId = block.id;

    const eligible = isCartEligible(block, cart);
    if (!eligible) continue;

    const hasGiftFromBlock = giftLines.some(
      (line) => line.properties && line.properties[FREE_GIFT_BLOCK_PROP] === String(blockId)
    );

    if (hasGiftFromBlock) continue;

    // Handle product type - show simple add button
    if (block.type === 'product' && block.productId) {
      const variantId = await getVariantIdFromProduct(block.productId);
      if (!variantId) {
        console.warn('[Free Gift] Could not resolve product ID:', block.productId);
        continue;
      }

      // Fetch product details for display
      try {
        const productHandle = /^\d+$/.test(block.productId) ? null : block.productId;
        if (productHandle) {
          const productUrl = `${window.Shopify.routes.root}products/${productHandle}.json`;
          const productResponse = await fetch(productUrl);
          if (productResponse.ok) {
            const productData = await productResponse.json();
            const product = productData.product;
            const variant = product.variants.find(v => v.id === variantId) || product.variants[0];
            
            const container = document.createElement('div');
            container.className = 'cart-free-gift';
            container.dataset.blockId = blockId;

            container.innerHTML = `
              <div class="cart-free-gift__body">
                <a href="/products/${product.handle}" class="cart-free-gift__image-link">
                  <img src="${product.featured_image || product.images?.[0]?.src || ''}" alt="${product.title}">
                </a>
                <div class="cart-free-gift__content">
                  <div class="cart-free-gift__header">
                    <span class="cart-free-gift__badge product-badge__item">${window.theme?.strings?.free || 'Free'}</span>
                    <p class="cart-free-gift__title cart__item__title">${product.title}</p>
                  </div>
                  <button
                    type="button"
                    class="cart-free-gift__add btn btn--secondary btn--small"
                    data-free-gift-add
                    data-variant-id="${variantId}"
                    data-quantity="${block.quantity || 1}"
                  >
                    ${window.theme?.strings?.free_gift_add || 'Add free gift'}
                  </button>
                </div>
              </div>
            `;

            placeholder.appendChild(container);
            
            // Bind click handler
            const addBtn = container.querySelector('[data-free-gift-add]');
            if (addBtn) {
              addBtn.addEventListener('click', async () => {
                addBtn.disabled = true;
                addBtn.classList.add('is-loading');
                try {
                  justAddedGift = true; // Set flag to prevent immediate correction
                  await addGiftVariant(variantId, blockId, block.quantity || 1);
                  // Show message if configured
                  if (block.message) {
                    showFreeGiftMessage(block.message);
                  }
                  // Clear flag after a delay to allow cart to update
                  setTimeout(() => {
                    justAddedGift = false;
                  }, 1000);
                  refreshThemeCartUI();
                } catch (e) {
                  console.error('Add free gift failed', e);
                  justAddedGift = false; // Clear flag on error
                  addBtn.disabled = false;
                } finally {
                  addBtn.classList.remove('is-loading');
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('[Free Gift] Error rendering product gift:', error);
      }
      continue;
    }

    // Handle collection type (existing logic)
    if (block.type !== 'collection' && !block.collectionHandle) {
      continue; // Skip if not collection and no collectionHandle (legacy support)
    }

    const collectionHandle = block.collectionHandle || (block.type === 'collection' ? block.collectionHandle : null);
    if (!collectionHandle) continue;

    const products = await fetchCollectionProducts(collectionHandle);

    if (!products || products.length === 0) {
      console.warn('[Free Gift] No products found in collection:', collectionHandle);
      continue;
    }

    let product = null;
    for (const p of products) {
      if ((p.variants || []).some(v => v.available)) {
        product = p;
        break;
      }
    }
    if (!product) product = products[0];
    const variants = product.variants || [];
    const hasMultipleVariants = variants.length > 1;

    const container = document.createElement('div');
    container.className = 'cart-free-gift';
    container.dataset.blockId = blockId;

    container.innerHTML = `
      <div class="cart-free-gift__body">
        <a href="/products/${product.handle}" class="cart-free-gift__image-link">
          <img src="${product.featured_image || product.images?.[0]?.src || ''}" alt="${product.title}">
        </a>
        <div class="cart-free-gift__content">
          <div class="cart-free-gift__header">
            <span class="cart-free-gift__badge product-badge__item">${window.theme?.strings?.free || 'Free'}</span>
            <p class="cart-free-gift__title cart__item__title">${product.title}</p>
          </div>
          ${hasMultipleVariants ? `
            <div class="cart-free-gift__variants">
              ${variants.map((v) => `
                <button
                  type="button"
                  class="cart-free-gift__variant"
                  data-variant-id="${v.id}"
                  ${!v.available ? 'disabled' : ''}
                >
                  ${v.title}
                </button>
              `).join('')}
            </div>
          ` : ''}
          <button
            type="button"
            class="cart-free-gift__add btn btn--secondary btn--small"
            data-free-gift-add
            data-variant-id="${variants[0]?.id || ''}"
            ${hasMultipleVariants ? 'disabled' : ''}
          >
            ${window.theme?.strings?.free_gift_add || 'Add free gift'}
          </button>
        </div>
      </div>
    `;

    placeholder.appendChild(container);
    enhanceAccessibility(container, hasMultipleVariants);
  }
}

/**
 * Accessibility improvements for free gift UI.
 */
function enhanceAccessibility(container, hasMultipleVariants) {
  if (!container) return;

  if (hasMultipleVariants) {
    const variantsContainer = container.querySelector('.cart-free-gift__variants');
    if (variantsContainer) {
      variantsContainer.setAttribute('role', 'radiogroup');
      variantsContainer.setAttribute('aria-label', 'Select gift variant');
    }

    const variantButtons = container.querySelectorAll('.cart-free-gift__variant');
    variantButtons.forEach((btn) => {
      const variantTitle = btn.textContent.trim();
      btn.setAttribute('aria-label', `Select ${variantTitle}`);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
    });
  }

  const addBtn = container.querySelector('[data-free-gift-add]');
  if (addBtn) {
    const productTitle = container.querySelector('.cart-free-gift__title')?.textContent || 'gift';
    addBtn.setAttribute('aria-label', `Add ${productTitle} to cart`);
  }
}

/**
 * Delegated click handling for variant selection and add button.
 */
function bindManualGiftEvents() {
  document.addEventListener('click', async (event) => {
    const variantBtn = event.target.closest('.cart-free-gift__variant');
    if (variantBtn) {
      const container = variantBtn.closest('.cart-free-gift');
      const allVariants = container.querySelectorAll('.cart-free-gift__variant');

      allVariants.forEach((btn) => {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-checked', 'false');
      });
      variantBtn.classList.add('is-selected');
      variantBtn.setAttribute('aria-checked', 'true');

      const addBtn = container.querySelector('[data-free-gift-add]');
      if (addBtn) addBtn.disabled = false;
      return;
    }

    const addBtn = event.target.closest('[data-free-gift-add]');
    if (!addBtn) return;

    const container = addBtn.closest('.cart-free-gift');
    if (!container) return;

    const config = getFreeGiftConfig();
    if (!config) return;

    const blockId = container.dataset.blockId ?? container.dataset.blockIndex;

    let variantId = null;

    const selected = container.querySelector('.cart-free-gift__variant.is-selected');
    if (selected) {
      variantId = Number(selected.dataset.variantId);
    } else {
      const fallbackId = addBtn.dataset.variantId;
      if (fallbackId) {
        variantId = Number(fallbackId);
      }
    }

    if (!variantId) {
      console.warn('[Free Gift] No variant selected or available for add');
      return;
    }

    // Find block config to get message
    const block = config.blocks.find(b => b.id === blockId);

    addBtn.disabled = true;
    addBtn.classList.add('is-loading');

    try {
      justAddedGift = true; // Set flag to prevent immediate correction
      await addGiftVariant(variantId, blockId, block?.quantity || 1);
      // Show message if configured
      if (block && block.message) {
        showFreeGiftMessage(block.message);
      }
      // Clear flag after a delay to allow cart to update
      setTimeout(() => {
        justAddedGift = false;
      }, 1000);
      refreshThemeCartUI();
    } catch (e) {
      console.error('Add free gift failed', e);
      justAddedGift = false; // Clear flag on error
      addBtn.disabled = false;
    } finally {
      addBtn.classList.remove('is-loading');
    }
  });
}

/**
 * Called whenever the cart has been rendered or updated.
 * Debounced to avoid thrashing.
 */
async function handleCartUpdated() {
  const config = getFreeGiftConfig();
  if (!config) {
    console.warn('[Free Gift] No config found in handleCartUpdated');
    return;
  }

  if (debounceTimer) clearTimeout(debounceTimer);

  debounceTimer = setTimeout(async () => {
    try {
      const cart = await fetchCart();
      const { mutated } = await correctFreeGifts(cart);
      const finalCart = mutated ? await fetchCart() : cart;
      await renderManualGifts(finalCart);
      disableFreeGiftQuantityControls();
    } catch (e) {
      console.error('[Free Gift] update failed', e);
    }
  }, 300);
}

/**
 * Disable quantity controls for free gift items in cart.
 */
function disableFreeGiftQuantityControls() {
  const freeGiftItems = document.querySelectorAll('[data-free-gift-item]');

  freeGiftItems.forEach((item) => {
    const quantityInput = item.querySelector('[data-quantity-field]');
    if (quantityInput) {
      quantityInput.setAttribute('readonly', 'readonly');
      quantityInput.style.cursor = 'not-allowed';
    }

    const quantityButtons = item.querySelectorAll('[data-quantity-button]');
    quantityButtons.forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.style.cursor = 'not-allowed';
    });

    // Optional: disable remove button if you don't want customers to remove gifts manually
    // const removeBtn = item.querySelector('[data-item-remove]');
    // if (removeBtn) {
    //   removeBtn.style.pointerEvents = 'none';
    //   removeBtn.style.opacity = '0.5';
    // }
  });
}

/**
 * Show free gift message in cart
 */
function showFreeGiftMessage(message) {
  if (!message || !message.trim()) return;

  // Remove existing message if any
  const existingMessage = document.querySelector('[data-theme-free-gift-message]');
  if (existingMessage) {
    existingMessage.remove();
  }

  // Create message element
  const messageEl = document.createElement('div');
  messageEl.setAttribute('data-theme-free-gift-message', '');
  messageEl.className = 'theme-free-gift-message';
  messageEl.textContent = message;

  // Try to insert in cart
  const cartContent = document.querySelector('[data-api-content]');
  if (cartContent) {
    // Insert at the top of cart content
    cartContent.insertBefore(messageEl, cartContent.firstChild);
  } else {
    // Fallback: append to body temporarily
    document.body.appendChild(messageEl);
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.remove();
      }
    }, 5000);
  }

  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (messageEl.parentNode) {
      messageEl.style.opacity = '0';
      messageEl.style.transition = 'opacity 0.3s';
      setTimeout(() => {
        if (messageEl.parentNode) {
          messageEl.remove();
        }
      }, 300);
    }
  }, 5000);
}

/**
 * Initialise the free gift module and bind to theme cart events.
 */
function initFreeGift() {
  if (window.__freeGiftInit) {
    return;
  }
  window.__freeGiftInit = true;

  bindManualGiftEvents();

  // Initial run on page load (cart page)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      handleCartUpdated();
    });
  } else {
    // DOM already loaded
    handleCartUpdated();
  }

  // Re-run after cart is added to (product added)
  document.addEventListener('theme:cart:added', () => {
    handleCartUpdated();
  });

  // Re-run after cart is updated (quantity changed, item removed)
  document.addEventListener('theme:cart:update', () => {
    handleCartUpdated();
  });

  // Re-run after cart drawer is initially loaded
  document.addEventListener('theme:cart:loaded', () => {
    handleCartUpdated();
  });

  // Also listen for cart:updated (alternative event name)
  document.addEventListener('cart:updated', () => {
    handleCartUpdated();
  });
}

initFreeGift();
